<template>
    <Modal id="cash-up-modal"
           size="md"
           title="CASH_UP.TITLE"
           icon="fa-money-bill-1">
        <div class="cash-up">
            <div class="cash-up__headings">
                <div></div>
                <div>{{ l('CASH_UP.COUNT') }}</div>
                <div>{{ l('CASH_UP.VALUE') }}</div>

                <div class="desktop-only"></div>
                <div class="desktop-only">
                    {{ l('CASH_UP.COUNT') }}
                </div>
                <div class="desktop-only">
                    {{ l('CASH_UP.VALUE') }}
                </div>
            </div>

            <div class="cash-up__denominations">
                <div v-for="d in cashUpData"
                     :key="d.denominationID">
                    <div class="cash-up__denomination-value">
                        {{ d.denominationName }}
                    </div>
                    <div class="cash-up__denomination-count">
                        <input v-model="d.counted"
                               placeholder="0"
                               type="number"
                               min="0"
                               oninput="validity.valid || (value='');"
                               class="form-control input-md"
                               @input="recalculate" />
                    </div>
                    <div class="cash-up__denomination-total">
                        {{ formatValue(d.value) }}
                    </div>
                </div>
            </div>
            <div class="cash-up__total flex">
                <strong class="flex--grow flex--shrink">{{ l('CASH_UP.TOTAL') }}</strong>
                <span>{{ formatValue(total) }}</span>
            </div>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           :disabled="submittingCashUp"
                           @click="reject">
                    {{ l('CASH_UP.CANCEL') }}
                </SquareBtn>

                <SquareBtn size="lg"
                           class="btn-success"
                           :loading="submittingCashUp"
                           @click="submit">
                    {{ l('CASH_UP.OK') }}
                </SquareBtn>
            </div>
        </template>
    </Modal>
</template>

<script>
import BigNumber from 'bignumber.js';
import Modal from '@/vue/components/Modals/Modal';
import SquareBtn from '@/vue/components/SquareBtn';
import TradingSessionEvent from '@/lib/TradingSessions/TradingSessionEvent';
import TradingSessionEventType from '@/lib/TradingSessions/TradingSessionEventType';
import { container } from '@/container';
import { formatCurrency } from '@/lib/Formatting/currency';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'CashUpModal',

    components: { SquareBtn, Modal },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        tradingSessionID: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            count: true,
            // An array of all the possible denominations and the count entered for each.
            // This is populated in the created() method with an array entry for each denomination
            // the currency uses. The item shown here is just a demo to see the structure.
            cashUpData: [
                {
                    denominationName: '',
                    denominationValue: '',
                    counted: 0,
                    value: 0,
                },
            ],
            submittingCashUp: false,
        };
    },

    computed: {
        /**
         * Tally up the values and make a nice number.
         *
         * @return {Number}
         */
        total() {
            return (this.cashUpData.reduce((acc, cur) => acc + Number(cur.value), 0)).toFixed(2);
        },
    },

    /**
     * Build the cashUpData array on mount, using the denominations computed property.
     */
    created() {
        this.cashUpData = this.buildEmptyCashUpData();
    },

    methods: {
        /**
         * Builds the cashUpData array.
         *
         * @return {{denominationValue: *, denominationID: *, denominationName: *, counted: null, value: number}[]}
         */
        buildEmptyCashUpData() {
            return container.userService.currentUser.company.currency.currencyDenominations.map((denomination) => {
                return {
                    denominationID: denomination.ID,
                    denominationName: denomination.name,
                    denominationValue: denomination.value,
                    counted: null,
                    value: 0,
                };
            });
        },

        /**
         * Display the values in the correct format.
         *
         * @param {number} value
         *
         * @return  {string} nicely formatted amount or '£0.00'
         */
        formatValue(value) {
            return formatCurrency(container.currencyService, value);
        },

        recalculate() {
            this.cashUpData.forEach((d) => {
                if (d.counted) {
                    const value = new BigNumber(d.denominationValue);
                    d.value = value.multipliedBy(d.counted).toNumber();
                } else {
                    d.value = null;
                }
            });
        },

        /**
         * Sends the data off to tradingSessionManager.
         *
         * @param denominations
         *
         * @return {string}
         */
        saveCashUp(denominations) {
            container.tradingSessionManager.saveEvent(
                (new TradingSessionEvent(TradingSessionEventType.SESSION_CASH))
                    .setOperatorID(container.userService.getUserID())
                    .setDenominations(denominations)
                    .setTradingSessionID(this.tradingSessionID),
            );
        },

        async submit() {
            const denominations = this.cashUpData.map((line) => {
                return {
                    denominationID: line.denominationID,
                    qty: parseInt(line.counted, 10),
                    value: line.value,
                };
            });

            container.vueModalFactory.open(
                MODAL_NAMES__EPOS.CASH_UP_CONFIRM,
                {
                    total: this.total,
                },
            ).then(async () => {
                this.submittingCashUp = true;
                this.saveCashUp(denominations);
                // Give the queue a bit of time to submit the cash up before we resolve,
                // then reload the trading session.
                timeoutPromise(2000).then(() => {
                    this.resolve(true);
                });
            }).catch(() => {
                // The user cancelled.
                return null;
            });
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../../less/variables.less";

.cash-up {
    &__headings,
    &__denominations {
        display: grid;
        grid-template-columns: 1fr 100px 120px;
    }

    &__headings {
        > div {
            font-weight: bold;
            text-align: left;
            padding: 0 10px 10px;
        }
    }

    &__denominations {
        border-top: 1px solid @btn-default-border;
        border-left: 1px solid @btn-default-border;

        > div {
            // Vue complains if you use v-for on a <template> element.
            // So each denomination is a div. Use display:contents to its children are displayed in the grid.
            display: contents;
        }
    }

    &__denomination-value,
    &__denomination-count,
    &__denomination-total {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 10px;
        border-bottom: 1px solid @btn-default-border;
    }

    &__denomination-value {
        font-weight: bold;
    }

    &__denomination-total {
        text-align: right;
        border-right: 1px solid @btn-default-border;
    }

    &__total {
        margin: 15px 11px 0;
        font-size: 20px;
    }
}

@media (max-width: @mobile-max) {
    .cash-up {
        &__denominations {
            > div:nth-child(odd) {
                > div {
                    background-color: @table-bg-accent;
                }
            }
        }
    }
}

@media (min-width: @non-mobile-min) {
    .cash-up {
        &__headings,
        &__denominations {
            grid-template-columns: 1fr 100px 100px 1fr 100px 100px;
        }

        &__denominations {
            > div:nth-child(4n + 3),
            > div:nth-child(4n + 4) {
                > div {
                    background-color: @table-bg-accent;
                }
            }
        }
    }
}
</style>
